'use client'

import type { GenerateDeliveriesResponse } from '@/features/kitchen/api/generateDispatchDeliveries'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import {
  buildGenerateSummary,
  deliveryLineCount,
  deliveryQtyTotal,
  hasHeldLines,
  hasStockSkip,
  resolveGenerateCtas,
  type GenerateCta,
} from '@/features/kitchen/lib/generateResult'

type Props = {
  open: boolean
  result: GenerateDeliveriesResponse
  selectedOutletCount: number
  tripLabel?: string
  onClose: () => void
  onViewShipment: () => void
  onToProduction: () => void
}

function ctaHandler(
  cta: GenerateCta,
  handlers: Pick<Props, 'onClose' | 'onViewShipment' | 'onToProduction'>,
): () => void {
  switch (cta) {
    case 'view_shipment':
      return handlers.onViewShipment
    case 'to_production':
      return handlers.onToProduction
    case 'close':
      return handlers.onClose
  }
}

export function GenerateResultSheet({
  open,
  result,
  selectedOutletCount,
  tripLabel,
  onClose,
  onViewShipment,
  onToProduction,
}: Props) {
  if (!open) return null

  const t = kitchenTerms.dispatch.generateResult
  const deliveries = result.deliveries ?? []
  const skipped = result.skipped_outlets ?? []
  const storeName = deliveries[0]?.store_name ?? skipped[0]?.store_name ?? 'Toko'

  const title =
    selectedOutletCount === 1 ? t.titleSingle(storeName) : t.titleMulti(selectedOutletCount)

  let summary: string
  if (selectedOutletCount === 1 && deliveries.length > 0) {
    const d = deliveries[0]
    summary = t.summaryReady(d.store_name, deliveryLineCount(d), deliveryQtyTotal(d))
  } else if (deliveries.length > 0) {
    summary = t.summaryMulti(deliveries.length, skipped.length)
  } else {
    summary = t.summaryNone
  }

  const { tone } = buildGenerateSummary({ result, selectedOutletCount })
  const showWarning = hasStockSkip(result) || hasHeldLines(result)
  const { primary, secondary } = resolveGenerateCtas(result)
  const onPrimary = ctaHandler(primary, { onClose, onViewShipment, onToProduction })
  const onSecondary = secondary
    ? ctaHandler(secondary, { onClose, onViewShipment, onToProduction })
    : null

  const ctaLabels: Record<GenerateCta, string> = {
    view_shipment: t.ctaView,
    to_production: t.ctaProduction,
    close: t.ctaClose,
  }

  const summaryToneClass =
    tone === 'warning'
      ? 'border-status-warning/40 bg-status-warning-bg/30 text-status-warning'
      : tone === 'success'
        ? 'border-status-success/30 bg-surface-container-low text-on-surface'
        : 'border-outline-variant bg-surface-container-low text-on-surface'

  const panelBorderClass = showWarning
    ? 'border-status-warning/40'
    : 'border-outline-variant'

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generate-result-sheet-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/45 sheet-backdrop"
        onClick={onClose}
        aria-label={t.ctaClose}
      />
      <div
        className={`relative w-full max-w-md mx-auto rounded-t-2xl bg-surface-card border border-b-0 shadow-[0_-8px_32px_rgba(0,0,0,0.12)] overflow-hidden sheet-panel ${panelBorderClass}`}
      >
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-outline-variant" aria-hidden />
        <div className="px-gutter-tablet pt-4 pb-3 border-b border-surface-variant">
          <h2 id="generate-result-sheet-title" className="text-headline-md text-primary">
            {title}
          </h2>
          {tripLabel ? (
            <p className="text-body-sm text-on-surface-variant mt-1">{t.tripSubtitle(tripLabel)}</p>
          ) : null}
        </div>
        <div className="px-gutter-tablet py-4 space-y-4 max-h-[min(60vh,420px)] overflow-y-auto">
          <p className={`rounded-xl border px-3 py-2.5 text-body-sm font-medium ${summaryToneClass}`}>
            {summary}
          </p>

          {deliveries.length > 0 ? (
            <section className="space-y-2">
              <h3 className="text-label-md font-bold uppercase tracking-wide text-on-surface-variant">
                {t.sectionCreated}
              </h3>
              <ul className="space-y-2">
                {deliveries.map((delivery) => {
                  const lines = deliveryLineCount(delivery)
                  const qty = deliveryQtyTotal(delivery)
                  const held = delivery.heldLines ?? []

                  return (
                    <li
                      key={delivery.delivery_id}
                      className="rounded-xl border border-outline-variant bg-surface-container-low px-3 py-2.5 space-y-2"
                    >
                      <div>
                        <p className="text-body-sm font-semibold text-on-surface">
                          {delivery.store_name}
                        </p>
                        <p className="text-body-sm text-on-surface-variant">
                          {t.deliveryRow(delivery.delivery_code, lines, qty)}
                        </p>
                      </div>
                      {held.length > 0 ? (
                        <div className="rounded-lg border border-status-warning/40 bg-status-warning-bg/30 px-3 py-2 space-y-2">
                          <p className="text-body-sm text-status-warning font-medium">
                            {t.heldBanner(delivery.store_name, lines, held.length)}
                          </p>
                          <div>
                            <p className="text-label-md font-bold uppercase tracking-wide text-status-warning">
                              {t.heldSection}
                            </p>
                            <ul className="mt-1 space-y-1">
                              {held.map((line) => (
                                <li
                                  key={`${delivery.delivery_id}-${line.productName}`}
                                  className="text-body-sm text-status-warning"
                                >
                                  {t.heldRow(line.productName, line.needQty)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            </section>
          ) : null}

          {skipped.length > 0 ? (
            <section className="space-y-2">
              <h3 className="text-label-md font-bold uppercase tracking-wide text-on-surface-variant">
                {t.sectionSkipped}
              </h3>
              <ul className="space-y-2">
                {skipped.map((outlet) => (
                  <li
                    key={outlet.store_name}
                    className="rounded-xl border border-outline-variant bg-surface-container-low px-3 py-2.5"
                  >
                    <p className="text-body-sm font-semibold text-on-surface">{outlet.store_name}</p>
                    <p className="text-body-sm text-on-surface-variant mt-0.5">{outlet.reason}</p>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
        <div className="px-gutter-tablet pb-[max(1rem,env(safe-area-inset-bottom))] pt-0 flex flex-col gap-2">
          <button
            type="button"
            className="btn-primary w-full min-h-[52px] btn-tactile"
            onClick={onPrimary}
          >
            {ctaLabels[primary]}
          </button>
          {secondary && onSecondary ? (
            <button type="button" className="btn-outline w-full min-h-[44px]" onClick={onSecondary}>
              {ctaLabels[secondary]}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
